import React, { useState, useEffect, useCallback } from 'react';
import { X, Ban, Loader, Check, AlertTriangle } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

// DO NOT CONTACT - one decision that stops every kind of outreach to a person.
//
// Rachael, 2026-10-01, on a reply saying a customer's mother had passed away: "Do we have a way to
// remove anyone who comes back with responses like this?" Greg: "Don't close the account. Just
// stop the contact. And yes, stop all emails. And text if they were set up on text."
//
// CPx stops at once (win-back, Delivered/Placed by day offers, handwritten-note follow-ups, across
// their linked accounts). Dotdigital - newsletter and texts - follows within about ten minutes and
// the list below says whether it really happened. Logic: netlify/functions/lib/do-not-contact.js.

const REASONS = [
  ['passed_away', 'Passed away'],
  ['asked_to_stop', 'Asked us to stop'],
  ['spam_complaint', 'Reported our email as spam'],
  ['other', 'Other'],
];

const who = () => {
  const u = getCurrentUser() || {};
  return { adminId: u.id, repName: u.staffName || null };
};

export function DncModal({ customer, source, defaultReason = 'asked_to_stop', onClose, onDone }) {
  const [reason, setReason] = useState(defaultReason);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await neonQuery('dnc_flag', Object.assign({
        customerId: customer.customerId, customerName: customer.name || null, email: customer.email || null,
        reason, note, source: source || null,
      }, who()));
      onDone && onDone(r);
    } catch (e) { setErr(e.message || 'Could not save it.'); setBusy(false); }
  };

  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', zIndex: 4100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(480px,100%)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: '#7a271a', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Ban size={17} />
          <div style={{ fontWeight: 800, fontSize: 15 }}>Do not contact</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '16px 18px', fontSize: 13.5, color: 'var(--gray-700)', lineHeight: 1.55 }}>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>{customer.name || customer.email}</div>
          {customer.name && customer.email && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{customer.email}</div>}
          <div style={{ margin: '10px 0 12px' }}>
            This stops <b>all</b> outreach to them: VIP offers, win-back, note follow-ups, the newsletter and texts,
            including any other account that belongs to the same person. Their account stays open and they can still order.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {REASONS.map(([k, label]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="radio" name="dnc-reason" checked={reason === k} onChange={() => setReason(k)} /> {label}
              </label>
            ))}
          </div>
          <input
            value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) - e.g. daughter wrote in"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', borderRadius: 6, border: '1px solid var(--gray-300)' }}
          />
          {err && <div style={{ color: '#b42318', marginTop: 10 }}>{err}</div>}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray-300)', background: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button disabled={busy} onClick={go} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#b42318', color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader size={13} className="spin" /> : <Ban size={13} />} Stop all contact
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDay = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Dotdigital outcome for one row, in words.
function ddStatus(r) {
  // Spam complainers: Dotdigital already refuses to email them. Nothing was checked for text.
  if (r.dd_result && r.dd_result.already) return { ok: true, text: 'Email already stopped by Dotdigital' };
  if (r.dd_done_at) {
    const sms = Object.values((r.dd_result && r.dd_result.sms) || {});
    const texted = sms.some((x) => x && x.status < 300);
    return { ok: true, text: texted ? 'Email and text stopped' : 'Email stopped (not set up on text)' };
  }
  if (r.dd_attempts >= 6) return { ok: false, text: 'Dotdigital could not be updated - tell Greg' };
  if (r.dd_attempts > 0) return { ok: false, text: 'Dotdigital: retrying' };
  return { ok: null, text: 'Newsletter and texts stop within 10 minutes' };
}

// Everyone currently flagged, with the Dotdigital result and a way to lift the CPx block.
export function DncList({ refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const load = useCallback(async () => {
    try { setRows(await neonQuery('dnc_list', {})); } catch (_) { setRows([]); }
    try { setLastRun(await neonQuery('dnc_complaint_last_run', {})); } catch (_) { setLastRun(null); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const clear = async (r) => {
    const note = window.prompt(
      `Allow contact with ${r.customer_name || r.customer_id} again?

This lifts the block in CPx only. It does NOT put them back on the newsletter or texts - they have to opt back in themselves. Why? (optional)`, '');
    if (note === null) return;
    await neonQuery('dnc_clear', Object.assign({ customerId: r.customer_id, note }, who()));
    load();
  };

  if (!rows || !rows.length) return null;
  return (
    <div style={{ marginTop: 18 }}>
      <button onClick={() => setOpen((v) => !v)} style={{ border: 'none', background: 'none', color: 'var(--gray-600)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
        <Ban size={12} /> Do not contact ({rows.length}) {open ? '▾' : '▸'}
      </button>
      {open && lastRun && (
        <div style={{ marginTop: 6, fontSize: 11.5, fontWeight: lastRun.stale ? 700 : 400, color: lastRun.stale ? '#b42318' : 'var(--gray-500)' }}>
          {lastRun.stale
            ? `Spam complaint check has not run cleanly since ${lastRun.last_clean_at ? fmtDay(lastRun.last_clean_at) : 'it was set up'} - tell Greg`
            : `Spam complaints last checked ${fmtDay(lastRun.started_at)}: ${lastRun.flagged} added`}
        </div>
      )}
      {open && (
        <div style={{ marginTop: 8, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10 }}>
          {rows.map((r) => {
            const d = ddStatus(r);
            return (
              <div key={r.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ color: 'var(--navy)' }}>{r.customer_name || r.customer_id}</b>
                  <span style={{ color: 'var(--gray-500)' }}> · {(r.emails || []).join(', ')}</span>
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
                    {r.reasonLabel}{r.note ? ' - ' + r.note : ''} · {r.flagged_by_name || 'someone'}, {new Date(r.flagged_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: d.ok ? '#0b8043' : d.ok === false ? '#b42318' : 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                  {d.ok ? <Check size={11} /> : d.ok === false ? <AlertTriangle size={11} /> : null} {d.text}
                </div>
                <button onClick={() => clear(r)} style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>Allow again</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
